import React from 'react'
import { isNil } from 'ramda'
import { SelectItem } from '@ariakit/react'

import { optionStyle } from './style'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { SelectOptionProps } from './SelectOption.types'

/**
 * Component for rendering a single option in a Select or ChipSelect dropdown.
 *
 * @param testId - Adds a data-testid param to the trigger for Heap tracking and
 *   automated testing.
 * @param isMultiSelect - Whether the select allows multiple options to be
 *   selected.
 * @param leftMedia - Optional media element to display on the left side of the
 *   option.
 * @param optionClass - Additional CSS classes to apply to the option.
 * @param option - The option object containing value, label, sublabel, group,
 *   and optional leftMedia.
 * @returns {React.ReactNode} A styled option element for use in a select
 *   dropdown. It displays a checkmark for selected options in multi-select mode
 *   and includes an optional left media and sublabel elements.
 */
export const SelectOption = ({
  testId,
  isMultiSelect,
  leftMedia,
  option,
  optionClass,
}: SelectOptionProps) => {
  return (
    <SelectItem
      className={`${optionStyle} ${optionClass}`}
      value={option.value ?? ''}
      data-testid={testId}
      render={props => {
        return (
          <div {...props}>
            {isMultiSelect &&
              (props['aria-selected'] ? (
                <Icon
                  name="checkmark"
                  size={IconSize.SM}
                  className="color-icon-bold-default"
                />
              ) : (
                <div className="w-4 shrink-0" data-testid="info_unchecked" />
              ))}
            {option.leftMedia ? option.leftMedia : leftMedia}
            <div className="w-full shrink-0">
              {option.label}
              {!isNil(option.sublabel) && (
                <div className="sh-body-xs">{option.sublabel}</div>
              )}
            </div>
          </div>
        )
      }}
    />
  )
}
